<template>
  <div class="post-view" v-if="post">
    <div class="head-margin"></div>
    <div class="header-container">
      <div class="title">
        <typeWord>
          <h1>
            {{ post.name }}
          </h1>
        </typeWord>
      </div>
      <div class="date">
        <span class="calender">
          <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-calendar-time" width="24" height="24" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
              <path stroke="none" d="M0 0h24v24H0z"></path>
              <path d="M11.795 21h-6.795a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v4"></path>
              <circle cx="18" cy="18" r="4"></circle>
              <path d="M15 3v4"></path>
              <path d="M7 3v4"></path>
              <path d="M3 11h16"></path>
              <path d="M18 16.496v1.504l1 1"></path>
              </svg>
        </span>
        <span class="time">{{ post.time }}</span>
      </div>
      <div class='tags'>
        <tagBase v-for='tag in post.tags' :key="tag.name" :style="{backgroundColor: getTagColor(tag)}">
          {{tag.name}}
        </tagBase>

      </div>
    </div>

    <div class="main-content-layout">
      <div class="content-wrapper card">
        <div class="markdown-body" v-html="safeContent"></div>
        <div class="content-end">
          <hr>
        </div>
      </div>

      <aside class="toc-sidebar" v-if="headings.length > 0">
        <h3>内容导航</h3>
        <ul>
          <li v-for="heading in headings" :key="heading.id" 
              :class="{ 'active': activeHeadingId === heading.id, [`level-${heading.level}`]: true }">
            <a :href="`#${heading.id}`" @click.prevent="scrollToHeading(heading.id)">
              {{ heading.text }}
            </a>
          </li>
        </ul>
      </aside>
    </div>

    <div class="page-link row">
      <div class="col-5 pre-page" v-if="prevPost">
          <div class="card" @click="goToPost(prevPost.id)">
              <span class="arrow">←</span>
              <div class="page-label">上一篇</div>
              <div class="page-title">{{ prevPost.name }}</div>
          </div>
      </div>
      <div class="col-5 next-page" v-if="nextPost">
        <div class="card" @click="goToPost(nextPost.id)">
            <div class="page-label">下一篇</div>
            <div class="page-title">{{ nextPost.name }}</div>
            <span class="arrow">→</span>
        </div>
      </div>
    </div>
    

    <div class="footer" style='height: 100px'>
      <div class="copyright">
        © 2025 powered AWAY
      </div>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify';
import typeWord from '../components/typeWord.vue';
import tagBase from '@/components/tagBase.vue';
import router from '@/router/index'; 

export default {
    name: 'postView', 
    components: { 
      typeWord, 
      tagBase,
    },
    data() {
        return {
          postContent: null, 
          post: null, 
          posts: [], 
          tagColorMap: {}, 
          headings: [], 
          activeHeadingId: null, 
          isScrolling: 0, 
          prevPost: null,
          nextPost: null,
          scrollTimer: null, 
        }
    },
    computed: {
      safeContent() {
            if (this.$markdown) { 
                return DOMPurify.sanitize(this.$markdown.render(this.postContent || ''));
            }
            return '';
      },
    },
    methods: {
      goToPost(postId) {
        console.log("Navigating to post:", postId);
        router.push({ name: 'post', params: {id: postId} });
      },
      getTagColor(tag) { 
          return this.tagColorMap[tag.name] || this.tagColorMap['default']; 
      },
      
      extractHeadings() {
        this.headings = [];
        this.$nextTick(() => {
          const contentContainer = this.$el.querySelector('.markdown-body');
          if (contentContainer) {
            contentContainer.querySelectorAll('h1[id], h2[id], h3[id], h4[id]').forEach(h => {
              const clonedH = h.cloneNode(true); 
              const anchor = clonedH.querySelector('.header-anchor');
              if (anchor) {
                anchor.remove(); 
              }
              this.headings.push({
                id: h.id,
                text: clonedH.textContent.trim(), 
                level: parseInt(h.tagName.substring(1)), 
              });
            });
            
            this.setupAnchorClickHandler();
            this.handleScroll();
          }
        });
      },

      handleScroll() {
        if (this.isScrolling > 0) return;

        const scrollY = window.scrollY + 120; 

        let currentId = null;

        for (let i = this.headings.length - 1; i >= 0; i--) {
            const heading = this.headings[i];
            const element = document.getElementById(heading.id);
            
            if (element && element.offsetTop <= scrollY) {
                currentId = heading.id;
                break; 
            }
        }

        const isBottom = (window.innerHeight + window.scrollY) >= document.body.offsetHeight - 50;
        if (isBottom && this.headings.length > 0) {
            currentId = this.headings[this.headings.length - 1].id;
        }

        if (currentId) {
            this.activeHeadingId = currentId;
        } else if (this.headings.length > 0 && window.scrollY < 100) {
            this.activeHeadingId = this.headings[0].id;
        }
      },
      
      scrollToHeading(id) {
        const element = document.getElementById(id);
        if (element) {
          let offset = 385; 
          
          this.isScrolling += 1; 
          this.activeHeadingId = id; 

          window.scrollTo({
            top: element.offsetTop + offset,
            behavior: 'smooth',
          });
          
          setTimeout(() => {
            this.isScrolling -= 1; 
          }, 600); 

        } else {
          console.warn('Element with ID not found:', id);
        }
      },
      
      setupAnchorClickHandler() {
        const contentContainer = this.$el.querySelector('.markdown-body');
        if (contentContainer) {
          if (this._anchorClickHandler) { 
              contentContainer.removeEventListener('click', this._anchorClickHandler);
          }

          this._anchorClickHandler = (event) => {
            const target = event.target;
            if (target.tagName === 'A' && target.getAttribute('href') && target.getAttribute('href').startsWith('#')) {
              event.preventDefault(); 
              const id = target.getAttribute('href').substring(1); 
              this.scrollToHeading(id); 
            }
          };
          contentContainer.addEventListener('click', this._anchorClickHandler);
        }
      },

      async loadPostData(postId) {
        try {
          const postsResponse = await fetch('/posts/list.json'); 
          if (!postsResponse.ok) {
            throw new Error(`HTTP error! status: ${postsResponse.status}`);
          }
          this.posts = await postsResponse.json();

          const tagMapResponse = await fetch('/tags/tagmap.json'); 
          if (!tagMapResponse.ok) {
            throw new Error(`HTTP error! status: ${tagMapResponse.status}`);
          }
          this.tagColorMap = await tagMapResponse.json();

          const post = this.posts.find(p => p.id === Number(postId)); 
          
          if (post && post.pagePath) {
            console.log('Loading markdown for:', post.pagePath); 
            const markdownResponse = await fetch(`/posts/${post.pagePath}.md`); 
            if (!markdownResponse.ok) {
              throw new Error(`HTTP error! status: ${markdownResponse.status}`);
            }
            this.post = post; 
            this.postContent = await markdownResponse.text();

            const currentPostIndex = this.posts.findIndex(p => p.id === post.id);
            this.prevPost = currentPostIndex > 0 ? this.posts[currentPostIndex - 1] : null;
            this.nextPost = currentPostIndex < this.posts.length - 1 ? this.posts[currentPostIndex + 1] : null;

            this.$nextTick(() => {
              this.extractHeadings(); 
              this.processMarkdownEnhancements();
            });

          } else {
            router.push({name: "404"}); 
          }
        } catch (error) {
          console.error("加载文章或标签数据失败:", error);
          router.push({name: "404"}); 
        }
      },
      processMarkdownEnhancements() {
        const contentContainer = this.$el.querySelector('.markdown-body');
        if (!contentContainer) return;

        contentContainer.querySelectorAll('pre').forEach(pre => {
            if (pre.querySelector('.copy-btn')) return;
            
            const btn = document.createElement('button');
            btn.className = 'copy-btn';
            btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>`;
            
            btn.addEventListener('click', () => {
                const code = pre.querySelector('code');
                const text = code ? code.innerText : pre.innerText;
                navigator.clipboard.writeText(text).then(() => {
                    btn.classList.add('copied');
                    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
                    setTimeout(() => {
                        btn.classList.remove('copied');
                        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>`;
                    }, 2000);
                });
            });
            pre.appendChild(btn);
        });

        const alertIcons = {
            note: '<svg class="octicon octicon-info" viewBox="0 0 16 16" version="1.1" width="16" height="16" aria-hidden="true" fill="currentColor"><path d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Zm8-6.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM6.5 7.75A.75.75 0 0 1 7.25 7h1a.75.75 0 0 1 .75.75v2.75h.25a.75.75 0 0 1 0 1.5h-2a.75.75 0 0 1 0-1.5h.25v-2h-.25a.75.75 0 0 1-.75-.75ZM8 6a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z"></path></svg>',
            tip: '<svg class="octicon octicon-light-bulb" viewBox="0 0 16 16" version="1.1" width="16" height="16" aria-hidden="true" fill="currentColor"><path d="M8 1.5c-2.363 0-4 1.69-4 3.75 0 .984.424 1.625.984 2.304l.214.253c.223.264.47.556.673.848.284.411.537.896.621 1.49a.75.75 0 0 1-1.484.211c-.04-.282-.163-.547-.37-.847a8.456 8.456 0 0 0-.542-.68c-.084-.1-.173-.205-.268-.32C3.201 7.75 2.5 6.766 2.5 5.25 2.5 2.31 4.863 0 8 0s5.5 2.31 5.5 5.25c0 1.516-.701 2.5-1.328 3.259-.095.115-.184.22-.268.319-.207.245-.383.453-.541.681-.208.3-.33.565-.37.847a.751.751 0 0 1-1.485-.212c.084-.593.337-1.078.621-1.489.203-.292.45-.584.673-.848.075-.088.147-.173.213-.253.561-.679.985-1.32.985-2.304 0-2.06-1.637-3.75-4-3.75ZM5.75 12h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1 0-1.5ZM6 15.25a.75.75 0 0 1 .75-.75h2.5a.75.75 0 0 1 0 1.5h-2.5a.75.75 0 0 1-.75-.75Z"></path></svg>',
            important: '<svg class="octicon octicon-report" viewBox="0 0 16 16" version="1.1" width="16" height="16" aria-hidden="true" fill="currentColor"><path d="M0 1.75C0 .784.784 0 1.75 0h12.5C15.216 0 16 .784 16 1.75v9.5A1.75 1.75 0 0 1 14.25 13H8.06l-2.573 2.573A1.458 1.458 0 0 1 3 14.543V13H1.75A1.75 1.75 0 0 1 0 11.25Zm1.75-.25a.25.25 0 0 0-.25.25v9.5c0 .138.112.25.25.25h2a.75.75 0 0 1 .75.75v2.19l2.72-2.72a.75.75 0 0 1 .53-.22h6.5a.25.25 0 0 0 .25-.25v-9.5a.25.25 0 0 0-.25-.25Zm7 2.25v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 1.5 0ZM9 9a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z"></path></svg>',
            warning: '<svg class="octicon octicon-alert" viewBox="0 0 16 16" version="1.1" width="16" height="16" aria-hidden="true" fill="currentColor"><path d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575Zm1.763.707a.25.25 0 0 0-.44 0L1.698 13.132a.25.25 0 0 0 .22.368h12.164a.25.25 0 0 0 .22-.368Zm.53 3.996v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 1.5 0ZM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z"></path></svg>',
            caution: '<svg class="octicon octicon-stop" viewBox="0 0 16 16" version="1.1" width="16" height="16" aria-hidden="true" fill="currentColor"><path d="M4.47.22A.749.749 0 0 1 5 0h6c.199 0 .389.079.53.22l4.25 4.25c.141.14.22.331.22.53v6a.749.749 0 0 1-.22.53l-4.25 4.25A.749.749 0 0 1 11 16H5a.749.749 0 0 1-.53-.22L.22 11.53A.749.749 0 0 1 0 11V5c0-.199.079-.389.22-.53Zm.84 1.28L1.5 5.31v5.38l3.81 3.81h5.38l3.81-3.81V5.31L10.69 1.5ZM8 4a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 8 4Zm0 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z"></path></svg>'
        };

        contentContainer.querySelectorAll('blockquote').forEach(bq => {
            const firstP = bq.querySelector('p');
            if (!firstP) return;
            
            const text = firstP.textContent;
            const match = text.match(/^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]/i);
            
            if (match) {
                const type = match[1].toLowerCase();
                bq.classList.add('markdown-alert', `markdown-alert-${type}`);
                
                firstP.innerHTML = firstP.innerHTML.replace(/^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s?/i, '');
                
                const titleDiv = document.createElement('div');
                titleDiv.className = 'markdown-alert-title';
                if (alertIcons[type]) {
                    titleDiv.innerHTML = alertIcons[type];
                }
                titleDiv.innerHTML += type.charAt(0).toUpperCase() + type.slice(1);
                bq.insertBefore(titleDiv, firstP);
            }
        });
      },
    },
    watch: {
      '$route.params.id': {
        immediate: true, 
        handler(newId) {
          if (newId) {
            this.loadPostData(newId); 
          }
        }
      }
    },
    async mounted() {
      console.log('PostView mounted for ID:', this.$route.params.id);
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    },
    beforeUnmount() {
      window.removeEventListener('scroll', this.handleScroll);
      
      const contentContainer = this.$el.querySelector('.markdown-body');
      if (contentContainer && this._anchorClickHandler) {
          contentContainer.removeEventListener('click', this._anchorClickHandler);
      }
    }, 
}
</script>

<style scoped>

.page-link {
  height: 100px; 
  display: flex; 
  margin: 0 auto;
  width: 90%; 
  max-width: 1200px; 
  justify-content: space-between; 
}

.pre-page {
  height: 100%; 
  padding: 0; 
  display: flex;
  width: 48%; 
  margin-right: auto; 
}

.next-page {  
  height: 100%; 
  padding: 0; 
  display: flex; 
  width: 48%; 
  margin-left: auto; 
}

/* 底部上一篇/下一篇卡片增加毛玻璃背景 */
.next-page .card,
.pre-page .card {
    position: relative;
    height: 100%;
    width: 100%;
    background-color: rgba(24, 28, 39, 0.5); /* 替换为半透明色 */
    backdrop-filter: blur(12px); /* 毛玻璃模糊 */
    -webkit-backdrop-filter: blur(12px); /* 兼容 Safari */
    padding: 20px;
    cursor: pointer;
    overflow: hidden;
    border: 1px solid rgba(156, 197, 226, 0.3);
    border-radius: 12px; /* 添加圆角更贴合现代 UI */
    transition: all 0.3s ease-in-out;
}

.next-page .card:hover,
.pre-page .card:hover {
    border-color: #585877;
    box-shadow: 0px 0px 10px #3c3f6d;
}

.next-page .page-label {
    text-align: right; 
}

.next-page .page-title {
    text-align: right; 
}

.page-label {
    color: #9cc5e2;
    font-size: 0.9em;
    margin-bottom: 8px;
}

.page-title {
    color: #e0e0e0;
    font-size: 1.1em;
    line-height: 1.6;
    max-height: 3em;
    overflow: hidden;
    white-space: nowrap; 
    text-overflow: hidden;
}

.arrow {
    position: absolute;
    color: #9cc5e2;
    font-size: 1.5em;
    top: 50%;
    transform: translateY(-65%);
    margin-bottom: 10%; 
}

.next-page .arrow {
    right: 20px;
}

.pre-page .arrow {
    left: 20px;
}

@media (max-width: 768px) {
    .page-title {
        font-size: 0.8em;
    }
}

/* 页面整体背景改为透明 */
.post-view { 
  padding-top: 50px; 
  background-color: transparent; /* 去除原本实色背景 */
}

.header-container { 
  margin-top: 150px; 
  margin-bottom: 150px; 
  text-align: center;
  color: #e0e0e0; 
}

.title { 
  color: #9cc5e2;
}

.date { 
  margin-top: 10px; 
}

.calender { 
  margin: 10px; 
}

.calender svg { 
  transform: translateY(-2px);
}

.main-content-layout {
  display: flex; 
  justify-content: center; 
  gap: 30px; 
  margin: 0 auto;
  width: 90%; 
  max-width: 1200px; 
  padding-bottom: 50px; 
}

/* 文章主体区域添加半透明及毛玻璃 */
.content-wrapper {
  flex-grow: 1; 
  min-width: 0; 
  background-color: rgba(37, 45, 56, 0.6); /* 替换为半透明色 */
  backdrop-filter: blur(12px); /* 毛玻璃模糊 */
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.3); /* 调整阴影增强质感 */
  border: 1px solid rgba(255, 255, 255, 0.05); /* 添加发光边缘 */
  border-radius: 12px; /* 圆角边缘更美观 */
  padding: 40px 60px 40px 60px;
  color: rgb(196, 198, 201);
}

.markdown-body ::v-deep {
  line-height: 1.7; 
  font-size: 16px; 
}

.markdown-body ::v-deep h1, 
.markdown-body ::v-deep h2, 
.markdown-body ::v-deep h3, 
.markdown-body ::v-deep h4, 
.markdown-body ::v-deep h5, 
.markdown-body ::v-deep h6 {
  position: relative; 
  color: #9cc5e2; 
  margin-top: 1.5em; 
  margin-bottom: 1em; 
  line-height: 1.3;
  font-weight: 600; 
  padding-bottom: 0.3em; 
  border-bottom: 1px solid rgba(156, 197, 226, 0.2); 
}
.markdown-body ::v-deep h1 { font-size: 2.2em; }
.markdown-body ::v-deep h2 { font-size: 1.8em; }
.markdown-body ::v-deep h3 { font-size: 1.5em; }
.markdown-body ::v-deep h4 { font-size: 1.2em; }

.markdown-body ::v-deep p {
  margin-bottom: 1em; 
}

.markdown-body ::v-deep ul, .markdown-body ::v-deep ol {
  margin-bottom: 1em;
  padding-left: 2em; 
}

.markdown-body ::v-deep li {
  margin-bottom: 0.5em;
}

.markdown-body ::v-deep a {
  color: #007bff; 
  text-decoration: none;
}

.markdown-body ::v-deep a:hover {
  text-decoration: underline;
}

.markdown-body ::v-deep pre {
  background-color: rgba(13, 13, 13, 0.7); /* 让代码块稍微透明 */
  border-radius: 6px;
  padding: 1em;
  overflow-x: auto; 
  margin-bottom: 1.5em;
  position: relative;
}

.markdown-body ::v-deep code {
  background-color: rgba(116, 143, 166, 0.2); 
  padding: 0.1em 0.2em;
  border-radius: 4px;
  font-family: 'Fira Code', 'Cascadia Code', monospace; 
  font-size: 0.9em;
  color: #e0e0e0;
}

.markdown-body ::v-deep pre code {
    background-color: transparent; 
    padding: 0;
    border-radius: 0;
    font-size: 1em;
}

.markdown-body ::v-deep .copy-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: #e0e0e0;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  opacity: 0;
}

.markdown-body ::v-deep pre:hover .copy-btn {
  opacity: 1;
}

.markdown-body ::v-deep .copy-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.markdown-body ::v-deep hr {
  height: 0.25em;
  padding: 0;
  margin: 24px 0;
  background-color: rgba(156, 197, 226, 0.2);
  border: 0;
}

/* GitHub Alerts */
.markdown-body ::v-deep .markdown-alert {
  padding: 0.5rem 1rem;
  margin-bottom: 1rem;
  border-left: 0.25em solid;
  background-color: rgba(24, 28, 39, 0.5);
  border-radius: 6px;
}

.markdown-body ::v-deep .markdown-alert-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 14px;
}

.markdown-body ::v-deep .markdown-alert-title svg {
  margin-right: 8px;
}

.markdown-body ::v-deep .markdown-alert-note { border-color: #1f6feb; }
.markdown-body ::v-deep .markdown-alert-note .markdown-alert-title { color: #58a6ff; }
.markdown-body ::v-deep .markdown-alert-tip { border-color: #238636; }
.markdown-body ::v-deep .markdown-alert-tip .markdown-alert-title { color: #3fb950; }
.markdown-body ::v-deep .markdown-alert-important { border-color: #8957e5; }
.markdown-body ::v-deep .markdown-alert-important .markdown-alert-title { color: #a371f7; }
.markdown-body ::v-deep .markdown-alert-warning { border-color: #9e6a03; }
.markdown-body ::v-deep .markdown-alert-warning .markdown-alert-title { color: #d29922; }
.markdown-body ::v-deep .markdown-alert-caution { border-color: #da3633; }
.markdown-body ::v-deep .markdown-alert-caution .markdown-alert-title { color: #f85149; }

.markdown-body ::v-deep .header-anchor {
  opacity: 0; 
  position: absolute; 
  left: -1.4em; 
  top: 10%; 
  padding-left: 0.5em;
  font-size: 0.8em; 
  text-decoration: none; 
  color: #a0a0a0; 
  transition: opacity 0.2s ease-in-out; 
  padding-right: 0.5em; 
}

.markdown-body ::v-deep h1:hover .header-anchor, 
.markdown-body ::v-deep h2:hover .header-anchor, 
.markdown-body ::v-deep h3:hover .header-anchor, 
.markdown-body ::v-deep h4:hover .header-anchor, 
.markdown-body ::v-deep h5:hover .header-anchor, 
.markdown-body ::v-deep h6:hover .header-anchor {
  opacity: 1; 
}

/* 侧边导航栏增加半透明毛玻璃 */
.toc-sidebar {
  width: 250px; 
  flex-shrink: 0; 
  position: sticky; 
  top: 80px; 
  height: auto; 
  max-height: calc(100vh - 80px - 20px); 
  overflow-y: auto; 
  padding: 16px;
  margin-left: 20px; 
  background-color: rgba(37, 45, 56, 0.6); /* 替换为半透明色 */
  backdrop-filter: blur(12px); /* 毛玻璃模糊 */
  -webkit-backdrop-filter: blur(12px); /* 兼容 Safari */
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.3); /* 调整阴影 */
  border: 1px solid rgba(255, 255, 255, 0.05); /* 添加发光边缘 */
  border-radius: 12px; 
  color: rgb(196, 198, 201);
}

.toc-sidebar h3 {
  color: #9cc5e2;
  margin-top: 0;
  margin-bottom: 1em;
  font-size: 1.2em;
  border-bottom: 1px solid rgba(156, 197, 226, 0.2);
  padding-bottom: 0.5em;
}

.toc-sidebar ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.toc-sidebar li {
  margin-bottom: 8px;
}

.toc-sidebar a {
  text-decoration: none;
  color: rgb(196, 198, 201);
  display: block;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease-in-out;
}

.toc-sidebar a:hover {
  color: #83adda; 
  background-color: rgba(0, 123, 255, 0.1); 
}

.toc-sidebar li.active a {
  color: #83adda; 
  background-color: rgba(0, 123, 255, 0.25); 
  border-left: 4px solid #6a6ce1; 
}

.toc-sidebar li.level-3 {
  padding-left: 15px;
}
.toc-sidebar li.level-4 {
  padding-left: 30px;
}

@media (max-width: 768px) {
  .main-content-layout {
    width: 95%; 
    gap: 20px;
  }

  .content-wrapper {
    width: 100%; 
    padding: 20px; 
  }

  .toc-sidebar {
    display: none;
  }
}
</style>